'use client';
import React, { useEffect, useRef, useState } from 'react';
import LLInput from '@next-app/components/elements/LLInput/LLInput';
import { CreditCardFormFields } from './CreditCardFormFields';
import { CHECKOUT_VALIDATION_MESSAGES } from '@next-app/constants/CheckoutConstants';
import {
  handlePaste,
  isNotNullOrUndefinedorEmpty,
} from '@next-app/utils/common-utils';
import { sendOnBlurDTMEvent } from '@next-app/utils/CheckoutHelper';

const ExpirationDateInput = ({
  keyVal,
  setFormData,
  existValue,
  setFormErrors,
  formError,
  formErrors,
}: {
  keyVal: string;
  setFormData: Function;
  existValue: string;
  setFormErrors: Function;
  formError: string;
  formErrors: Record<string, string>;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(0);
  const [error, setError] = useState('');
  const [expVal, setExpVal] = useState(existValue || '');
  const validateMonth = (newMonth: string) => {
    if (newMonth.length === 2) {
      const monthNum = parseInt(newMonth);
      if (monthNum < 1 || monthNum > 12) {
        setFormData((prevState: Record<string, string>) => ({
          ...prevState,
          [CreditCardFormFields.EXPIRATION_MONTH]: '',
        }));
        setError(CHECKOUT_VALIDATION_MESSAGES.CC_EXPIRY_INVALID);
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  };

  useEffect(() => {
    setExpVal(existValue);
  }, [existValue]);

  useEffect(() => {
    if (formError !== error && isNotNullOrUndefinedorEmpty(error)) {
      setFormErrors((prev: any) => {
        return { ...prev, expirationError: error };
      });
    } else if (error === '' && formError !== error) {
      setFormErrors((prev: any) => {
        return { ...prev, expirationError: '' };
      });
    }
    // else if (formError !== error && !isNotNullOrUndefinedorEmpty(error)) {
    //   setFormErrors((prev: any) => {
    //     return { ...prev, expirationError: error };
    //   });
    // }
  }, [error]);

  useEffect(() => {
    if (formError !== '') {
      setError(formError);
    }
  }, [formError]);

  const validateYear = (newYear: string, century: number) => {
    if (newYear.length === 2) {
      const fullYear = century + parseInt(newYear);
      const currentYear = new Date().getFullYear();
      if (fullYear < currentYear) {
        setFormData((prevState: Record<string, string>) => ({
          ...prevState,
          [CreditCardFormFields.EXPIRATION_YEAR]: 0,
        }));
        setError(CHECKOUT_VALIDATION_MESSAGES.CC_EXPIRED);
      }
    } else {
      setFormData((prevState: Record<string, string>) => ({
        ...prevState,
        [CreditCardFormFields.EXPIRATION_YEAR]: 0,
      }));
      setError('');
    }
  };

  const formatExpiry = (value: string, cursorPos: number) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as MM/DD
    // If value already contains a slash, leave it as is
    if (value.includes('/') && cursorPos < 2) {
      return value;
    } else if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }
  };

  function parseExpiry(value: string) {
    // Trim and normalize input
    const trimmed = value.trim();

    if (trimmed === '') return { month: null, year: null };

    // Handle single value: "12" or "/26"
    if (!trimmed.includes('/')) {
      // Just month
      return { month: trimmed.padStart(2, '0'), year: null };
    }

    // Handle formats like "/26", "2/26", "12/26"
    const [m, y] = trimmed.split('/');

    const month = m ? m.padStart(2, '0') : null;
    const year = y || null;

    return { month, year };
  }

  const handleInputChange = (e: any) => {
    let cursorPos = e.target.selectionStart;
    let expVals = formatExpiry(e.target.value, cursorPos);

    setError('');

    if (expVals.length > 5) {
      expVals = expVals.slice(0, 5);
    }

    let { month: newMonth, year: newYear } = parseExpiry(expVals);
    const currentYear = new Date().getFullYear();
    const century: number = Math.floor(currentYear / 100) * 100;
    setMonth(newMonth || '00');

    if (newYear) {
      const fullYear = +century + +newYear;
      setFormData((prevState: Record<string, string>) => ({
        ...prevState,
        [CreditCardFormFields.EXPIRATION_MONTH]: newMonth,
        [CreditCardFormFields.EXPIRATION_YEAR]: fullYear.toString(),
      }));
      setYear(fullYear);
    } else {
      setYear(0);
    }

    setExpVal(expVals);

    if (!expVals) {
      setFormData((prevState: Record<string, string>) => ({
        ...prevState,
        [CreditCardFormFields.EXPIRATION_MONTH]: '',
        [CreditCardFormFields.EXPIRATION_YEAR]: 0,
      }));
    }

    setTimeout(() => {
      if (cursorPos === 3 && !expVal.includes('/')) {
        cursorPos++;
      }
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPos, cursorPos);
        return;
      }
    }, 0);
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setError('');
    let errorMessage = '';
    if (!value) errorMessage = CHECKOUT_VALIDATION_MESSAGES.CC_EXPIRY_MISSING;

    validateMonth(month);
    const currentYear = new Date().getFullYear();
    const century: number = Math.floor(currentYear / 100) * 100;
    //Validate year (optional: ensure it's not in the past)

    let newYear = year.toString().slice(2);

    const currentMonth = new Date().getMonth() + 1;
    const expYear = +century + +newYear;
    let newMonth = parseInt(month);
    if (
      (isNotNullOrUndefinedorEmpty(expYear) &&
        newMonth >= 1 &&
        newMonth <= 12 &&
        expYear < currentYear) ||
      (expYear === currentYear && parseInt(month) < currentMonth)
    ) {
      setError(CHECKOUT_VALIDATION_MESSAGES.CC_EXPIRED);
    }
    if (errorMessage) {
      setError(errorMessage);
    }
    sendOnBlurDTMEvent({ key: 'ccExpiryDate', errorMessage, value });
  };

  return (
    <LLInput
      type="text"
      id={keyVal}
      key={keyVal}
      onChange={handleInputChange}
      onPaste={handlePaste}
      ref={inputRef}
      maxLength={5}
      placeholder="Expiration Date (MM/YY)"
      className={error ? 'input-error' : ''}
      errorMsg={error}
      onBlur={onBlurHandler}
      value={expVal}
    />
  );
};

export default ExpirationDateInput;
